<template>
  <div
    class="h-[100dvh] max-h-[100dvh] bg-pitch-black overflow-y-auto font-inter flex flex-col text-total-white"
  >
    <!-- Header -->
    <TopNavbar />
    <!-- Progress Bar -->
    <div
      v-if="progressBarWidth != '0%'"
      class="h-1 bg-prime-golden transition-all duration-300 w-full z-2"
      :style="{ width: progressBarWidth }"
    ></div>

    <!-- Email Form -->
    <div
      class="bg-eerie-black flex flex-col flex-grow overflow-y-auto"
      :class="{
        'opacity-40': isEmailSending || isFetching,
        'pointer-events-none': isEmailSending || isFetching,
      }"
    >
      <!-- Header Section -->
      <div class="flex justify-between items-center p-4 mb-3">
        <h1 class="text-xl font-bold">{{ heading }}</h1>
        <div class="flex space-x-4">
          <!-- Cancel Button -->
          <button @click="cancelEmail" class="text-prime-golden text-lg">Cancel</button>
          <!-- Attach and Send Icons -->
          <button @click="triggerFileInput" class="text-prime-golden text-lg">
            <img
              class="w-4 mb-1"
              src="@/assets/images/attachment-icon.svg"
              alt="Attach"
            />
          </button>
          <input
            type="file"
            ref="fileInput"
            @change="handleFileChange"
            multiple
            class="hidden"
          />
          <button @click="sendEmail">
            <img class="w-6 mb-1" src="../../assets/images/up-arrow.svg" alt="Send" />
          </button>
        </div>
      </div>

      <!-- Email Form Fields -->
      <div>
        <!-- To Field with Autocomplete -->
        <div
          ref="toDropdown"
          class="flex items-center border-b border-dark-grey bg-eerie-black gap-1 px-4 py-1"
        >
          <label class="block text-sm font-medium text-total-white">To:</label>
          <div class="relative w-full">
            <vue3-tags-input
              v-model="toEmailInput"
              :tags="toEmails?.map(getContactName)"
              :validate="(email) => customValidate(email, toEmails)"
              @on-tags-changed="handleToEmailsChange"
              placeholder="Add email addresses"
              @input="onToInput"
            />
            <!-- Dropdown for Contacts -->
            <div
              v-if="contactDropdownOpen.to"
              class="absolute top-full left-0 w-full text-left bg-eerie-black border border-dark-grey z-10 max-h-40 overflow-y-auto"
            >
              <ul>
                <li
                  v-for="contact in getFilteredContacts('to')"
                  :key="contact.email"
                  @click.prevent="addToEmail(contact.email, 'to')"
                  class="p-2 hover:bg-dark-grey cursor-pointer"
                >
                  {{ getContactNameOrEmail(contact) }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- CC Field with Autocomplete -->
        <div
          ref="ccDropdown"
          class="flex items-center border-b border-dark-grey bg-eerie-black gap-1 px-4 py-1"
        >
          <label class="block text-sm font-medium text-total-white">CC:</label>
          <div class="relative w-full">
            <vue3-tags-input
              v-model="ccEmailInput"
              :tags="ccEmails.map(getContactName)"
              @on-tags-changed="handleCcEmailsChange"
              :validate="(email) => customValidate(email, toEmails)"
              placeholder="Add email addresses"
              @input="onCcInput"
            />
            <!-- Dropdown for Contacts -->
            <div
              v-if="contactDropdownOpen.cc"
              class="absolute top-full left-0 w-full text-left bg-eerie-black border border-dark-grey z-10 max-h-40 overflow-y-auto"
            >
              <ul>
                <li
                  v-for="contact in getFilteredContacts('cc')"
                  :key="contact.email"
                  @click.prevent="addToEmail(contact.email, 'cc')"
                  class="p-2 hover:bg-dark-grey cursor-pointer"
                >
                  {{ getContactNameOrEmail(contact) }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- BCC Field with Autocomplete -->
        <div
          ref="bccDropdown"
          class="flex items-center border-b border-dark-grey bg-eerie-black gap-1 px-4 py-1"
        >
          <label class="block text-sm font-medium text-total-white">BCC:</label>
          <div class="relative w-full">
            <vue3-tags-input
              v-model="bccEmailInput"
              :tags="bccEmails.map(getContactName)"
              @on-tags-changed="handleBccEmailsChange"
              :validate="(email) => customValidate(email, toEmails)"
              placeholder="Add email addresses"
              @input="onBccInput"
            />
            <!-- Dropdown for Contacts -->
            <div
              v-if="contactDropdownOpen.bcc"
              class="absolute top-full left-0 w-full text-left bg-eerie-black border border-dark-grey z-10 max-h-40 overflow-y-auto"
            >
              <ul>
                <li
                  v-for="contact in getFilteredContacts('bcc')"
                  :key="contact.email"
                  @click.prevent="addToEmail(contact.email, 'bcc')"
                  class="p-2 hover:bg-dark-grey cursor-pointer"
                >
                  {{ getContactNameOrEmail(contact) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <!-- Subject Field -->
        <div
          class="flex items-center border-b border-dark-grey bg-eerie-black gap-1 px-4"
        >
          <label class="block text-sm font-medium text-total-white">Subject:</label>
          <input
            v-model="subject"
            class="bg-eerie-black w-full py-2 text-base outline-none focus:border-prime-golden"
            type="text"
            placeholder="Subject"
          />
        </div>

        <!-- Project Field -->
        <div
          :class="[
            'flex relative items-center border-b border-dark-grey gap-1 px-4',
            projectDropdownOpen ? 'bg-dark-grey py-4' : 'bg-eerie-black py-2',
          ]"
        >
          <label
            :class="[
              'block text-total-white',
              projectDropdownOpen ? 'text-base' : 'text-sm',
            ]"
            >Project:</label
          >

          <div class="relative w-full bg-transparent px-2 flex items-center">
            <!-- Fixed Project Name or Dropdown -->
            <div
              v-if="false"
              class="w-full text-left text-sm font-semibold text-total-white"
            >
              {{ fixedProjectName }}
            </div>
            <div class="w-full" v-else>
              <!-- When dropdown is closed -->
              <div
                v-if="!projectDropdownOpen"
                @click="toggleProjectDropdown"
                class="w-full flex justify-between items-center cursor-pointer"
              >
                <span class="text-sm font-semibold text-total-white">{{
                  selectedProject
                }}</span>
                <i class="fas fa-chevron-down text-prime-golden"></i>
              </div>

              <!-- When dropdown is open -->
              <div v-else class="w-full flex items-center">
                <input
                  v-model="projectSearchQuery"
                  type="text"
                  placeholder="Search projects..."
                  class="w-full bg-transparent border-none text-total-white outline-none"
                />
                <button
                  @click="closeProjectDropdown"
                  class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Project Dropdown List -->
          <div
            v-if="projectDropdownOpen"
            class="absolute top-12 left-0 w-full text-left bg-eerie-black border border-dark-grey mt-2 z-10"
          >
            <ul>
              <li
                v-for="project in filteredProjects"
                :key="project.id"
                @click="selectProject(project)"
                class="p-4 bg-charcoal-grey border-b border-light-grey-translucent-dark hover:bg-dark-grey cursor-pointer"
              >
                {{ project.address_1 }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Message Input Area -->
      <div class="bg-pitch-black flex-1 flex flex-col overflow-y-auto">
        <TiptapEditor
          v-model="messageBody"
          placeholder="Start your message here"
          :on-image-upload="handleInlineImageUpload"
        ></TiptapEditor>

        <!-- Attached Files -->
        <div
          v-if="attachments.length"
          class="mb-4 flex flex-col justify-start items-start gap-2 px-4"
        >
          <h3 class="text-sm font-medium">Attachments:</h3>
          <div class="flex flex-wrap gap-1 flex-1">
            <span
              v-for="(file, index) in attachments"
              :key="index"
              class="flex items-center bg-dark-grey text-sm px-2 py-1 rounded"
            >
              {{ file.name }}
              <button @click="removeAttachment(index)" class="ml-1">
                <i class="fa fa-close text-total-white"></i>
              </button>
            </span>
          </div>
        </div>
        <!-- Thread Preview Section -->
        <div v-if="sortedThreadMessages.length" class="border-t border-dark-grey mt-4">
          <div
            v-for="log in sortedThreadMessages"
            :key="log.id"
            class="bg-eerie-black border-b border-dark-grey pt-4"
          >
            <div class="flex justify-between items-start px-4">
              <div class="flex flex-col items-start max-w-[70vw]">
                <!-- Safeguard for log.sender -->
                <h2 class="font-semibold">
                  <span
                    v-for="email in log.sender.split(',')"
                    :key="email"
                    @click="openContactPage(email.trim())"
                  >
                    {{ getStakeHolderNameByEmail(email.trim())
                    }}{{
                      email !== log.sender.split(",")[log.sender.split(",").length - 1]
                        ? ", "
                        : ""
                    }}
                  </span>
                </h2>

                <!-- Safeguard for log.to -->
                <div class="text-sm flex flex-wrap text-total-white-700">
                  <span class="text-total-white mr-1">To:</span>
                  <span
                    v-for="email in (log.to || '').split(',')"
                    :key="email"
                    @click="openContactPage(email.trim())"
                  >
                    {{ getStakeHolderNameByEmail(email.trim()) }}
                    {{
                      email !==
                      (log.to || "").split(",")[(log.to || "").split(",").length - 1]
                        ? ", "
                        : ""
                    }}
                  </span>
                </div>

                <!-- Safeguard for log.cc -->
                <div class="text-sm flex flex-wrap text-total-white-700">
                  <span class="text-total-white mr-1">CC:</span>
                  <span
                    v-for="email in (log.cc || '').split(',')"
                    :key="email"
                    @click="openContactPage(email.trim())"
                  >
                    {{ getStakeHolderNameByEmail(email.trim()) }}
                    {{
                      email !==
                      (log.cc || "").split(",")[(log.cc || "").split(",").length - 1]
                        ? ", "
                        : ""
                    }}
                  </span>
                </div>
              </div>
              <div class="flex flex-col items-end gap-4">
                <p class="text-sm text-right text-total-white">
                  {{ formatDateTime(log.time) }}
                </p>
              </div>
            </div>

            <!-- Project and Metadata -->
            <div class="flex items-center text-sm px-4 py-2">
              <span>
                Project: <bold class="font-semibold">{{ log.project }}</bold>
              </span>
            </div>

            <!-- Email Subject -->
            <h3 class="font-semibold text-left px-4 text-xl py-2">
              {{ log.subject }}
            </h3>

            <!-- Email Body -->
            <div
              class="text-base leading-6 text-start px-4 py-4 pt-6 mt-2 whitespace-pre-wrap break-words bg-eerie-black pb-10"
            >
              <div v-html="log.body"></div>
            </div>

            <!-- File Attachments -->
            <!-- <div
              v-if="log.files?.length > 0"
              class="flex flex-col gap-4 px-4 py-2 bg-eerie-black pb-4"
            >
              <span class="text-sm text-total-white text-left">Attachments:</span>
              <div
                v-for="file in log.files"
                :key="file.file_name"
                class="text-sm text-total-white"
              >
                <div
                  v-if="file.file_type?.startsWith('image/')"
                  class="flex flex-col items-start gap-2 bg-eerie-black rounded-lg p-2"
                >
                  <p class="text-sm text-total-white text-left">{{ file.file_name }}</p>
                  <template v-if="file.file_type?.startsWith('image/')">
                    <img
                      v-if="file.file_type !== 'image/heic'"
                      :src="file.file_url"
                      alt="Attachment Image"
                      @click="openImageModal(file.file_url)"
                      class="cursor-pointer"
                    />
                    <video
                      v-else
                      :src="file.file_url"
                      autoplay
                      muted
                      loop
                      class="cursor-pointer max-w-[100vw] max-h-[100vh] object-contain z-2"
                      @click="openImageModal(file.file_url)"
                    ></video>
                  </template>
                </div>
                <div
                  v-else
                  class="flex items-center gap-2 bg-eerie-black rounded-lg p-2 inline-block max-w-max"
                >
                  <div class="flex flex-col items-start">
                    <span>{{ file.file_name }}</span>
                    <span>{{ formatFileSize(file.file_size) }}</span>
                  </div>
                  <a :href="file.file_url" download class="text-eerie-black">
                    <img src="../../assets/images/download.svg" class="w-[18px]" />
                  </a>
                </div>
              </div>
            </div> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import TiptapEditor from "@/components/TiptapEditor.vue";
import Vue3TagsInput from "vue3-tags-input";
import { useToast } from "vue-toastification";
import { useProjectStore } from "@/stores/project";
import { useContactsStore } from "@/stores/contact";
import emailService from "@/api/emailService";
import { useLogStore } from "@/stores/log";

const isFetching = ref(true);

// Router and Route
const router = useRouter();
const route = useRoute();

const toast = useToast();

// Stores
const projectStore = useProjectStore();
const contactsStore = useContactsStore();

// Check if accessed from a specific project
const fixedProjectId = Number(route.params.projectId || route.query.projectId) || null;
const fixedContactId = route.query.contactId || null;
const fixedEmail = route.query.email || null;

const logStore = useLogStore();

const selectedProjectId = ref(
  Number(route.params.projectId || route.query.projectId) || null
);
const isEmailSending = ref(false);

const progressBarWidth = ref("0%");

const fixedProjectName = ref("");
const heading = ref("");

const getContactName = (email) => {
  const contact = contactsStore.contacts.find((c) => c.email === email);

  if (contact?.first_name == "" && contact?.last_name == "") {
    return email;
  }

  return contact ? `${contact?.first_name} ${contact?.last_name}` : email;
};

const formatDateTime = (dateTimeString) => {
  const options = {
    year: "numeric",
    month: "short",
    weekday: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  return new Date(dateTimeString).toLocaleString(undefined, options);
};

const getStakeHolderNameByEmail = (email) => {
  //console.log("!!!contactss",contactsStore.contacts);
  const stakeholder = contactsStore.contacts.find((s) => s.email == email);
  if (stakeholder?.first_name == "" && stakeholder?.last_name == "") {
    return email;
  }
  return stakeholder ? `${stakeholder.first_name} ${stakeholder.last_name}` : email;
};
const parseEmails = (emails) => {
  if (!emails) return [];
  if (Array.isArray(emails)) return emails;
  return emails.split(/\s*,\s*/).filter((email) => email.length > 0);
};

const getContactNameOrEmail = (contact) => {
  if (contact?.first_name == "" && contact?.last_name == "") {
    return contact.email;
  }
  return `${contact?.first_name} ${contact?.last_name}`;
};

onMounted(async () => {
  // Initialize textarea resize
  autoResizeTextarea();

  // Set up thread ID from query params
  threadId.value = route.query.thread_id || null;
  if (threadId.value) {
    await logStore.fetchMessagesByThreadId(threadId.value, "asc");
  }

  // Fetch projects
  await fetchProjects();
  await fetchContacts();

  if (fixedProjectId) {
    // Pre-fill project
    const project = await projectStore.fetchProjectById(fixedProjectId);
    if (project) {
      selectedProject.value = project.address_1;
      selectedProjectId.value = project.id;
      await fetchContacts();
    }
  }

  if (fixedContactId && fixedEmail) {
    // Pre-fill contact in 'To' field
    toEmails.value.push(fixedEmail);
  }

  if (fixedContactId && !fixedEmail) {
    // Pre-fill contact in 'To' field by finding the contact in the store
    const contactIdToFind = fixedContactId.toString();
    const contact = contactsStore.contacts.find(
      (c) => c.id.toString() === contactIdToFind
    );

    if (contact) {
      // Pre-fill To field
      toEmails.value.push(contact.email);
    }
  }

  const { type, to, subject: querySubject } = route.query;

  // Initialize subject based on type
  subject.value = querySubject || "";

  if (type === "reply") {
    // For Reply, only use the original sender as the To email, clear CC and BCC
    toEmails.value = route.query.toEmails ? route.query.toEmails : [];
    ccEmails.value = [];
    bccEmails.value = [];
    heading.value = "Reply";
    selectedProject.value = route.query.project;
    selectedProjectId.value = route.query.projectId;
  } else if (type === "replyAll") {
    // Use query param "toEmails" if available; otherwise, fall back to "to"
    let replyToEmails = route.query.toEmails
      ? parseEmails(route.query.toEmails)
      : parseEmails(to);
    let replyCcEmails = parseEmails(route.query.ccEmail);
    let replyBccEmails = parseEmails(route.query.bccEmail);

    toEmails.value = replyToEmails;
    ccEmails.value = replyCcEmails;
    bccEmails.value = replyBccEmails;

    heading.value = "Reply All";
    selectedProject.value = route.query.project;
    selectedProjectId.value = route.query.projectId;
  } else if (type === "forward") {
    // For Forward, leave all fields as they are and set heading
    toEmails.value = [];
    ccEmails.value = [];
    bccEmails.value = [];
    heading.value = "Forward";
    selectedProject.value = route.query.project;
    selectedProjectId.value = route.query.projectId;
    messageBody.value = route.query.body;
    autoResizeTextarea();
  } else {
    heading.value = "New Email";
  }

  isFetching.value = false;
});

// Project fields
const projectDropdownOpen = ref(false);
const projectSearchQuery = ref("");
const selectedProject = ref("Select Project");
const projects = computed(() => projectStore.projects);

const MAX_TOTAL_ATTACHMENT_SIZE = 30 * 1024 * 1024;
// Initialize contactSearchQuery as an object with keys for 'to', 'cc', and 'bcc'
const contactSearchQuery = ref({
  to: "",
  cc: "",
  bcc: "",
});

// Dropdown state initialization for To, CC, and BCC
const contactDropdownOpen = ref({
  to: false,
  cc: false,
  bcc: false,
});

const textarea = ref(null);

const autoResizeTextarea = () => {
  const el = textarea.value;
  if (el) {
    el.style.height = "auto"; // Reset height to auto
    el.style.height = el.scrollHeight + "px"; // Set height to the scroll height
  }
};

const fetchProjects = async () => {
  if (fixedProjectId) {
    // Fetch specific project by ID
    await projectStore.fetchProjectById(fixedProjectId);
    fixedProjectName.value = projectStore.currentProject.address_1;
    selectedProject.value = projectStore.currentProject.address_1;
    selectedProjectId.value = projectStore.currentProject.id;
    await fetchContacts();
  } else {
    // Fetch all projects
    await projectStore.fetchAllProjects();

    // Check if there are any projects
    if (!projects.value.length) {
      // Show toast message
      toast.error(
        "Email Messages must be linked to a Project. You're not part of any projects."
      );

      // Redirect to Projects page after a short delay
      setTimeout(() => {
        router.push({ name: "ProjectsPage" });
      }, 3000); // Adjust delay as needed
    }
  }
};

const useClickOutside = (elementRef, callback) => {
  const clickHandler = (event) => {
    if (elementRef.value && !elementRef.value.contains(event.target)) {
      callback();
    }
  };

  onMounted(() => {
    document.addEventListener("click", clickHandler);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", clickHandler);
  });
};
const toDropdown = ref(null);
const ccDropdown = ref(null);
const bccDropdown = ref(null);

useClickOutside(toDropdown, () => (contactDropdownOpen.value.to = false));
useClickOutside(ccDropdown, () => (contactDropdownOpen.value.cc = false));
useClickOutside(bccDropdown, () => (contactDropdownOpen.value.bcc = false));

const customValidate = (email, existingTags) => {
  // Check if email is valid
  if (!validateEmail(email)) {
    return false;
  }

  // Check if the email already exists in the list
  if (existingTags.includes(email)) {
    return false;
  }

  return true;
};

const filteredProjects = computed(() => {
  return projects.value.filter((project) =>
    project.address_1?.toLowerCase().includes(projectSearchQuery?.value?.toLowerCase())
  );
});

// Toggle dropdown for projects
const toggleProjectDropdown = () => {
  projectDropdownOpen.value = true;
  projectSearchQuery.value = "";
};

// Close project dropdown
const closeProjectDropdown = () => {
  projectDropdownOpen.value = false;
  projectSearchQuery.value = "";
};

// Select project from the dropdown and fetch contacts related to the selected project
const selectProject = (project) => {
  selectedProject.value = project.address_1;
  projectDropdownOpen.value = false;
  selectedProjectId.value = project.id;
  projectSearchQuery.value = "";
  fetchContacts();
};

// Handlers for input fields (to, cc, bcc)
const onToInput = () => {
  contactSearchQuery.value.to = toEmailInput.value; // Ensure to use .value for reactive properties
  contactDropdownOpen.value.to = true;
};

const onCcInput = () => {
  contactSearchQuery.value.cc = ccEmailInput.value; // Update cc field in the contactSearchQuery object
  contactDropdownOpen.value.cc = true;
};

const onBccInput = () => {
  contactSearchQuery.value.bcc = bccEmailInput.value; // Update bcc field in the contactSearchQuery object
  contactDropdownOpen.value.bcc = true;
};

// Contacts for autocomplete
const contacts = computed(() => contactsStore.contacts);
const getFilteredContacts = (field) => {
  return contacts.value.filter(
    (contact) =>
      (contact?.first_name + " " + contact?.last_name)
        ?.toLowerCase()
        .includes(contactSearchQuery.value[field].toLowerCase()) && contact.email
  );
};

// Add email from dropdown
const addToEmail = (email, field) => {
  if (!validateEmail(email)) {
    toast.error(`Invalid email address: ${email}`);
    return;
  }

  if (field === "to") {
    if (toEmails.value.includes(email)) {
      toast.error(`The email "${email}" has already been added to the "To" field.`);

      return;
    }
    toEmails.value.push(email);
    contactDropdownOpen.value.to = false;
    toEmailInput.value = "";
    contactSearchQuery.value.to = "";
  } else if (field === "cc") {
    if (ccEmails.value.includes(email)) {
      toast.error(`The email "${email}" has already been added to the "CC" field.`);
      return;
    }
    ccEmails.value.push(email);
    contactDropdownOpen.value.cc = false;
    ccEmailInput.value = "";
    contactSearchQuery.value.cc = "";
  } else if (field === "bcc") {
    if (bccEmails.value.includes(email)) {
      toast.error(`The email "${email}" has already been added to the "BCC" field.`);
      return;
    }
    bccEmails.value.push(email);
    contactDropdownOpen.value.bcc = false;
    bccEmailInput.value = "";
    contactSearchQuery.value.bcc = "";
  }
};

// Fetch contacts related to the selected project or all contacts if no project is selected
const fetchContacts = async () => {
  if (fixedProjectId || selectedProject.value !== "Select Project") {
    const projectId =
      fixedProjectId ||
      projects.value.find((p) => p.address_1 === selectedProject.value)?.id;
    if (projectId) {
      await contactsStore.fetchContactsByProjectId(projectId);
    }
  } else {
    await contactsStore.fetchAllContacts();
  }
};
// Tags input values
const toEmailInput = ref("");
const ccEmailInput = ref("");
const bccEmailInput = ref("");

// Tags arrays
const toEmails = ref([]);
const ccEmails = ref([]);
const bccEmails = ref([]);

// Other fields
const subject = ref("");
const messageBody = ref("");
const attachments = ref([]);

const email = ref(null);

const threadId = ref(route.query.threadId || null);

const threadMessages = computed(() => {
  return logStore.threadMessages.map((log) => {
    const projectId = log.project_id;
    const project = projectStore.projects.find((p) => p.id === projectId);
    const participants = log.participants || [];

    const getParticipantInfo = (email) => {
      const participant = participants.find((p) => p.email === email);
      return participant
        ? `${participant.first_name} ${participant.last_name} (${participant.email})`
        : email;
    };

    const sender = getParticipantInfo(log.from_contact);
    const to = Array.isArray(log.to_contact)
      ? log.to_contact.map(getParticipantInfo).join(", ")
      : getParticipantInfo(log.to_contact);
    const cc = Array.isArray(log.cc_contact)
      ? log.cc_contact.map(getParticipantInfo).join(", ")
      : "";

    email.value = {
      id: log.id,
      threadId: log.thread_id,
      sender: sender,
      to: to,
      cc: cc,
      subject: log.subject || "No Subject",
      body: log.content || "No Content",
      time: log.date_time_created,
      project: project ? project.address_1 : "Loading...",
      files: log.files || [],
    };

    return email.value;
  });
});

const sortedThreadMessages = computed(() => {
  return [...threadMessages.value].sort((a, b) => new Date(b.time) - new Date(a.time));
});

// console.log(sortedThreadMessages.value, threadMessages, "sortedThreadMessages");
// Handle email changes for To, CC, and BCC
const handleToEmailsChange = (newTags) => {
  toEmails.value = newTags;
  contactSearchQuery.value.to = "";
};

const handleCcEmailsChange = (newTags) => {
  ccEmails.value = newTags;
  contactSearchQuery.value.cc = "";
};

const handleBccEmailsChange = (newTags) => {
  bccEmails.value = newTags;
  contactSearchQuery.value.bcc = "";
};

// Email validation function
const validateEmail = (email) => {
  const re = /^\S+@\S+\.\S+$/;
  return re.test(email);
};

// File input logic
const fileInput = ref(null);

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileChange = (event) => {
  const files = Array.from(event.target.files);
  for (const file of files) {
    // Check if adding this file exceeds total size limit
    const currentTotalSize = attachments.value.reduce((total, f) => total + f.size, 0);
    if (currentTotalSize + file.size > MAX_TOTAL_ATTACHMENT_SIZE) {
      toast.error(
        `Cannot add "${file.name}". Total attachments size cannot exceed ${
          MAX_TOTAL_ATTACHMENT_SIZE / (1024 * 1024)
        }MB.`
      );
      continue; // Skip adding this file
    }

    attachments.value.push(file);
  }

  event.target.value = null;
};

const removeAttachment = (index) => {
  attachments.value.splice(index, 1);
};

// Handle inline image upload for Tiptap editor
const handleInlineImageUpload = async (file) => {
  try {
    // Check file size
    const currentTotalSize = attachments.value.reduce((total, f) => total + f.size, 0);
    if (currentTotalSize + file.size > MAX_TOTAL_ATTACHMENT_SIZE) {
      toast.error(
        `Cannot add "${file.name}". Total attachments size cannot exceed ${
          MAX_TOTAL_ATTACHMENT_SIZE / (1024 * 1024)
        }MB.`
      );
      return null;
    }

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files can be pasted inline.');
      return null;
    }

    // Add to attachments list for email sending
    attachments.value.push(file);

    // Create a data URL for immediate display in the editor
    const dataUrl = await readFileAsBase64WithPrefix(file);
    return dataUrl;
  } catch (error) {
    console.error('Error handling inline image upload:', error);
    toast.error('Failed to upload image.');
    return null;
  }
};

// Helper function to read file as base64 with data URL prefix
const readFileAsBase64WithPrefix = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // This includes the data:image/... prefix
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const prepareAttachments = async () => {
  const attachmentList = [];
  for (const file of attachments.value) {
    const content = await readFileAsBase64(file);
    attachmentList.push({
      filename: file.name,
      content_type: file.type,
      content: content,
    });
  }
  return attachmentList;
};

const handleSendEmailError = (error) => {
  console.error("Error sending email:", error);

  let errorMessage = "Failed to send email.";

  if (error.response) {
    if (error.response.status === 413) {
      // Handle Payload Too Large error
      errorMessage =
        "The total size of the attachments is too large. Please reduce the size and try again.";
    } else if (error.response.data && error.response.data.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    }
  } else if (error.request) {
    // Network error occurred (status is 0)
    if (error.message === "Network Error" && attachments.value.length > 0) {
      errorMessage =
        "Failed to send email. The total size of the attachments might be too large. Please reduce the size and try again.";
    } else {
      errorMessage =
        "Network error occurred while sending the email. Please check your internet connection.";
    }
  } else {
    errorMessage = error.message || "An unknown error occurred while sending the email.";
  }

  // Display the specific error message
  toast.error(errorMessage);
  progressBarWidth.value = "0%";
  isEmailSending.value = false;
};

const sendEmail = async () => {
  if (!selectedProjectId.value) {
    toast.error("Please select a project before sending an email.");
    return;
  }

  try {
    // Check for unentered input text in To, CC, and BCC fields
    const unenteredToEmail = toEmailInput.value.trim();
    const unenteredCcEmail = ccEmailInput.value.trim();
    const unenteredBccEmail = bccEmailInput.value.trim();

    progressBarWidth.value = "10%";

    // Add unentered emails to their respective fields if valid
    if (
      unenteredToEmail &&
      !toEmails.value.includes(unenteredToEmail) &&
      validateEmail(unenteredToEmail)
    ) {
      toEmails.value.push(unenteredToEmail);
      toEmailInput.value = "";
    }

    if (
      unenteredCcEmail &&
      !ccEmails.value.includes(unenteredCcEmail) &&
      validateEmail(unenteredCcEmail)
    ) {
      ccEmails.value.push(unenteredCcEmail);
      ccEmailInput.value = "";
    }
    if (
      unenteredBccEmail &&
      !bccEmails.value.includes(unenteredBccEmail) &&
      validateEmail(unenteredBccEmail)
    ) {
      bccEmails.value.push(unenteredBccEmail);
      bccEmailInput.value = "";
    }

    if (!toEmails.value.length) {
      toast.error("Please add at least one recipient in the 'To' field.");
      return;
    }

    // Validate email addresses
    const allEmails = [...toEmails.value, ...ccEmails.value, ...bccEmails.value];
    for (const email of allEmails) {
      if (!email.includes("@")) {
        const contactExists = contacts.value.some(
          (contact) => contact.first_name + " " + contact.last_name == email
        );
        if (!contactExists) {
          toast.error(`No contact found with the name: ${email}`);
          return;
        } else {
          const theContact = contacts.value.find(
            (contact) => contact.first_name + " " + contact.last_name == email
          );
          const fullName = theContact.first_name + " " + theContact.last_name;
          if (toEmails.value.includes(fullName)) {
            toEmails.value = toEmails.value.filter((email) => email !== fullName);
            toEmails.value.push(theContact.email);
          } else if (ccEmails.value.includes(fullName)) {
            ccEmails.value = ccEmails.value.filter((email) => email !== fullName);
            ccEmails.value.push(theContact.email);
          } else if (bccEmails.value.includes(fullName)) {
            bccEmails.value = bccEmails.value.filter((email) => email !== fullName);
            bccEmails.value.push(theContact.email);
          }
        }
      } else if (!validateEmail(email)) {
        toast.error(`Invalid email address: ${email}`);
        return;
      }
    }

    // console.log("Selected Project ID: ", selectedProjectId.value);

    isEmailSending.value = true;

    const emailData = {
      recipients: toEmails.value,
      cc: ccEmails.value,
      bcc: bccEmails.value,
      subject: subject.value,
      body: messageBody.value,
      attachments: await prepareAttachments(),
      project_id: Number(selectedProjectId.value),
      thread_id: threadId.value ? Number(threadId.value) : null,
    };

    const response = await emailService.sendEmail(emailData);

    progressBarWidth.value = "100%";
    const existingThreadId = response.data.thread_id;
    toast.success("Email sent successfully!");

    if (existingThreadId) {
      router.push({
        name: "EmailViewPage",
        params: {
          projectId: selectedProjectId.value,
          threadId: existingThreadId,
          fromLog: true,
        },
        query: {
          thread_id: existingThreadId,
          from_contact: route.query.from_contact || "",
          currentTab: route.query.currentTab || "log",
        },
      });
    } else {
      router.push({
        name: "ProjectPage",
        params: { id: fixedProjectId || selectedProjectId.value },
        query: { tab: "log" },
      });
    }
    resetEmailForm();
    isEmailSending.value = false;
  } catch (error) {
    handleSendEmailError(error);
  }
};

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result.split(",")[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const resetEmailForm = () => {
  toEmailInput.value = "";
  ccEmailInput.value = "";
  bccEmailInput.value = "";
  toEmails.value = [];
  ccEmails.value = [];
  bccEmails.value = [];
  subject.value = "";
  messageBody.value = "";
  attachments.value = [];
};

const cancelEmail = () => {
  resetEmailForm();

  if (route.query.from_contact) {
    router.go(-1);
  } else if (route.query.projectId) {
    if (route.query.thread_id) {
      router.push({
        name: "EmailViewPage",
        params: { id: route.query.projectId },
        query: { thread_id: route.query.thread_id, tab: route.query.currentTab },
      });
    } else {
      router.push({
        name: "ProjectPage",
        params: { id: route.query.projectId },
        query: { tab: route.query.currentTab || "log" },
      });
    }
  } else {
    router.go(-1);
  }
};
</script>

<style>
.v3ti .v3ti-tag {
  @apply bg-dark-grey !important;
}
.v3ti {
  @apply border-none !important;
}
.v3ti .v3ti-tag .v3ti-remove-tag {
  color: #ffffff !important;
  transition: color 0.3s !important;
}
.v3ti-content {
  @apply bg-eerie-black !important;
  min-width: none !important;
}

.v3ti .v3ti-tag .v3ti-remove-tag:hover {
  color: #ffffff !important;
  border: none !important;
  @apply bg-dark-grey border-none !important;
}
.v3ti-remove-tag {
  color: #ffff !important;
}
.v3ti--focus {
  box-shadow: none !important;
}
</style>
